import React, { useState, useCallback } from 'react'
import { Link, router } from '@inertiajs/react'
import debounce from 'lodash/debounce'

export default function DataTable({
  data,
  columns,
  actions,
  searchable = false,
  searchPlaceholder = 'Search...',
  emptyState = { message: 'No items found' },
  baseUrl,
  params = {},
  pagination = null,
  rowHref = null,
}) {
  const [searchValue, setSearchValue] = useState(params.search || '')

  const navigate = useCallback(
    debounce((newParams) => {
      router.visit(baseUrl, {
        data: { ...params, ...newParams },
        preserveState: true,
        preserveScroll: true,
      })
    }, 300),
    [baseUrl, params]
  )

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchValue(value)
    navigate({ search: value || undefined, page: undefined })
  }

  const handleSort = (columnKey) => {
    const column = columns.find((c) => c.key === columnKey)
    if (!column?.sortable) return

    const newDirection =
      params.sort === columnKey && params.direction === 'asc' ? 'desc' : 'asc'
    router.visit(baseUrl, {
      data: {
        ...params,
        sort: columnKey,
        direction: newDirection,
        page: undefined,
      },
      preserveState: true,
      preserveScroll: true,
    })
  }

  const getSortIcon = (columnKey) => {
    if (params.sort !== columnKey) return null
    return params.direction === 'asc' ? '↑' : '↓'
  }

  const renderCellValue = (item, column) => {
    const value = item[column.key]
    if (column.render) {
      return column.render(value, item)
    }
    return value
  }

  const handlePageChange = (page) => {
    router.visit(baseUrl, {
      data: { ...params, page },
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <div>
      {/* Search */}
      {searchable && (
        <div className="mb-4">
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            className="w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 text-sm"
          />
        </div>
      )}

      {/* Table */}
      {data.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <p className="text-gray-500 mb-4">{emptyState.message}</p>
          {emptyState.action && (
            <Link
              href={emptyState.action.href}
              className="text-sky-600 hover:text-sky-500"
            >
              {emptyState.action.label}
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      column.sortable
                        ? 'cursor-pointer hover:bg-gray-100 select-none'
                        : ''
                    } ${column.headerClassName || ''}`}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <span className="flex items-center gap-1">
                      {column.label}
                      {column.sortable && (
                        <span className="text-gray-400">
                          {getSortIcon(column.key) || '↕'}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
                {actions && (
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item) => (
                <tr
                  key={item.id}
                  className={`hover:bg-gray-50 ${
                    rowHref ? 'cursor-pointer' : ''
                  }`}
                  onClick={
                    rowHref ? () => router.visit(rowHref(item)) : undefined
                  }
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        column.className || ''
                      }`}
                    >
                      {renderCellValue(item, column)}
                    </td>
                  ))}
                  {actions && (
                    <td
                      className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-3">
                        {actions(item)}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.prev)}
                  disabled={!pagination.prev}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.next)}
                  disabled={!pagination.next}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{pagination.page}</span>{' '}
                    of <span className="font-medium">{pagination.pages}</span> (
                    {pagination.count} total)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(pagination.prev)}
                      disabled={!pagination.prev}
                      className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.next)}
                      disabled={!pagination.next}
                      className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
